import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Asset } from '@/stores/assetStore';
import { Chart } from './Chart';
import { LoadingIndicator } from './LoadingIndicator';
import { ErrorDisplay } from './ErrorDisplay';
import { aktoolsService } from '@/services/aktools';
import { unifiedApiService } from '@/services/unifiedApi';
import type { TimeRange } from '@/types/api';
import { formatPrice } from '@/utils/formatPrice';

type Period = 'daily' | 'weekly' | 'monthly';

const PERIODS: Array<{ value: Period; label: string }> = [
  { value: 'daily', label: '日线' },
  { value: 'weekly', label: '周线' },
  { value: 'monthly', label: '月线' },
];

const normalizeHkStockSymbol = (asset: Asset): string | null => {
  const symbol = asset.symbol?.toUpperCase().trim();
  if (!symbol) return null;
  const match = symbol.match(/^(\d{1,5})(?:\.HK)?$/);
  if (match && match[1]) return match[1].padStart(5, '0');
  if (asset.exchange?.toUpperCase() === 'HK') {
    const digits = symbol.replace(/[^0-9]/g, '');
    if (/^\d{1,5}$/.test(digits)) return digits.padStart(5, '0');
  }
  return null;
};

const normalizeAStockSymbol = (asset: Asset): string | null => {
  const symbol = asset.symbol?.toUpperCase().trim();
  if (!symbol) return null;
  const code = symbol.replace(/\.(SH|SZ|BJ)$/i, '');
  if (/^\d{6}$/.test(code)) return code;
  return null;
};

const normalizeIndexSymbolCandidates = (asset: Asset): string[] | null => {
  const symbol = asset.symbol?.toUpperCase().trim();
  if (!symbol) return null;
  if (/^(SH|SZ)\d{6}$/.test(symbol)) return [symbol.toLowerCase()];
  if (/^\d{6}$/.test(symbol)) return [`sh${symbol}`, `sz${symbol}`];
  return null;
};

const normalizeBondSymbol = (asset: Asset): string | null => {
  const symbol = asset.symbol?.toUpperCase().trim();
  if (!symbol) return null;
  const code = symbol.replace(/\.(SH|SZ|BJ)$/i, '');
  if (/^\d{6}$/.test(code)) return code;
  return null;
};

const normalizeForexCandidates = (asset: Asset): string[] | null => {
  const symbol = asset.symbol?.toUpperCase().trim();
  if (!symbol) return null;

  let from = '';
  let to = '';
  if (symbol.includes('/')) {
    const parts = symbol.split('/');
    from = (parts[0] ?? '').trim();
    to = (parts[1] ?? '').trim();
  } else if (symbol.includes('_')) {
    const parts = symbol.split('_');
    from = (parts[0] ?? '').trim();
    to = (parts[1] ?? '').trim();
  } else if (/^[A-Z]{6}$/.test(symbol)) {
    from = symbol.slice(0, 3);
    to = symbol.slice(3);
  }

  if (!/^[A-Z]{3}$/.test(from) || !/^[A-Z]{3}$/.test(to)) return null;

  const base = `${from}${to}`;
  const candidates = [base, `${base}C`];
  if (to === 'CNY') {
    candidates.push(`${from}CNH`, `${from}CNHC`);
  }
  return candidates;
};

const formatYmd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
};

const CHART_POINTS_LIMIT = 120;

const getDateRangeByPeriod = (
  period: Period
): { start: string; end: string } => {
  const endDate = new Date();
  const daysBack =
    period === 'daily'
      ? CHART_POINTS_LIMIT
      : period === 'weekly'
        ? CHART_POINTS_LIMIT * 7
        : CHART_POINTS_LIMIT * 31;
  const startDate = new Date(
    endDate.getTime() - daysBack * 24 * 60 * 60 * 1000
  );
  return { start: formatYmd(startDate), end: formatYmd(endDate) };
};

const mapPeriodToTimeRange = (period: Period): TimeRange =>
  period === 'daily' ? '90D' : '1Y';

/** YY/MM/DD for chart axis and tooltip */
const formatDateLabel = (time: number, _mode: Period): string => {
  const d = new Date(time);
  if (isNaN(d.getTime())) return '';
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}/${mm}/${dd}`;
};

const getIsoWeekKey = (date: Date): string => {
  const tmp = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(
    ((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
  );
  return `${tmp.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

const aggregatePointsByAverage = (
  points: Array<{ time: number; value: number }>,
  period: Period
): Array<{ time: number; value: number }> => {
  if (period === 'daily') return points;
  const grouped = new Map<
    string,
    { time: number; sum: number; count: number }
  >();
  for (const point of points) {
    const d = new Date(point.time);
    if (isNaN(d.getTime())) continue;
    const key =
      period === 'weekly'
        ? getIsoWeekKey(d)
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const current = grouped.get(key);
    if (current) {
      current.sum += point.value;
      current.count += 1;
      if (point.time > current.time) current.time = point.time;
    } else {
      grouped.set(key, { time: point.time, sum: point.value, count: 1 });
    }
  }
  return Array.from(grouped.values())
    .map(item => ({
      time: item.time,
      value: item.count > 0 ? item.sum / item.count : 0,
    }))
    .filter(p => Number.isFinite(p.value) && p.value > 0)
    .sort((a, b) => a.time - b.time);
};

const sliceRecentPoints = (
  points: Array<{ time: number; value: number }>,
  limit: number
): Array<{ time: number; value: number }> => {
  if (points.length <= limit) return points;
  return points.slice(points.length - limit);
};

interface AktoolsHistoryPageProps {
  asset: Asset;
  onBack: () => void;
}

export const AktoolsHistoryPage = ({
  asset,
  onBack,
}: AktoolsHistoryPageProps) => {
  const [period, setPeriod] = useState<Period>('daily');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prices, setPrices] = useState<Array<{ time: number; value: number }>>(
    []
  );
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const hkSymbol = useMemo(() => normalizeHkStockSymbol(asset), [asset]);
  const aSymbol = useMemo(() => normalizeAStockSymbol(asset), [asset]);
  const indexCandidates = useMemo(
    () => normalizeIndexSymbolCandidates(asset),
    [asset]
  );
  const bondSymbol = useMemo(() => normalizeBondSymbol(asset), [asset]);
  const forexCandidates = useMemo(
    () => normalizeForexCandidates(asset),
    [asset]
  );
  const supportsPeriod =
    asset.type === 'stock' && (aSymbol !== null || hkSymbol !== null);

  const formatTimeLabel = useCallback(
    (time: number) => formatDateLabel(time, period),
    [period]
  );

  const fetchHistory = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      let points: Array<{ time: number; value: number }> = [];

      if (asset.type === 'stock') {
        if (hkSymbol) {
          const { data } = await aktoolsService.getJson<any[]>(
            '/api/public/stock_hk_daily',
            { symbol: hkSymbol, adjust: '' },
            { signal: controller.signal }
          );
          points = (Array.isArray(data) ? data : [])
            .map(row => ({
              time: new Date(String(row?.date ?? '')).getTime(),
              value: Number(row?.close ?? 0),
            }))
            .filter(
              p =>
                Number.isFinite(p.time) &&
                Number.isFinite(p.value) &&
                p.value > 0
            )
            .sort((a, b) => a.time - b.time);
          points = aggregatePointsByAverage(points, period);
          points = sliceRecentPoints(points, CHART_POINTS_LIMIT);
        } else if (aSymbol) {
          // A-share history uses investing only; handled via unifiedApiService fallback below.
        }
      } else if (asset.type === 'index' && indexCandidates) {
        let data: any[] = [];
        for (const candidate of indexCandidates) {
          const res = await aktoolsService.getJson<any[]>(
            '/api/public/stock_zh_index_daily_em',
            { symbol: candidate },
            { signal: controller.signal }
          );
          if (Array.isArray(res.data) && res.data.length > 0) {
            data = res.data;
            break;
          }
        }
        points = (Array.isArray(data) ? data : [])
          .map(row => ({
            time: new Date(String(row?.date ?? row?.['日期'] ?? '')).getTime(),
            value: Number(row?.close ?? row?.['收盘'] ?? 0),
          }))
          .filter(
            p =>
              Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0
          )
          .sort((a, b) => a.time - b.time);
        points = sliceRecentPoints(points, CHART_POINTS_LIMIT);
      } else if (asset.type === 'forex' && forexCandidates) {
        // Forex history is handled via unifiedApiService (investing_fx), skip legacy em endpoint.
      } else if (asset.type === 'bond' && bondSymbol) {
        let data: any[] = [];
        const primary = await aktoolsService.getJson<any[]>(
          '/api/public/bond_zh_hs_daily',
          { symbol: bondSymbol },
          { signal: controller.signal }
        );
        if (Array.isArray(primary.data) && primary.data.length > 0) {
          data = primary.data;
        } else {
          const fallback = await aktoolsService.getJson<any[]>(
            '/api/public/bond_zh_hs_cov_daily',
            { symbol: bondSymbol },
            { signal: controller.signal }
          );
          data = Array.isArray(fallback.data) ? fallback.data : [];
        }

        points = (Array.isArray(data) ? data : [])
          .map(row => ({
            time: new Date(String(row?.['日期'] ?? row?.date ?? '')).getTime(),
            value: Number(row?.['收盘'] ?? row?.['收盘价'] ?? row?.close ?? 0),
          }))
          .filter(
            p =>
              Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0
          )
          .sort((a, b) => a.time - b.time);
        points = sliceRecentPoints(points, CHART_POINTS_LIMIT);
      }

      if (!points || points.length === 0) {
        try {
          const timeRange = mapPeriodToTimeRange(period);
          const intervalMap: Record<Period, 'D' | 'W' | 'M'> = { daily: 'D', weekly: 'W', monthly: 'M' };
          const hist = await unifiedApiService.getHistoricalData(asset as any, timeRange, {
            timeout: 20000,
            context: `aktools-history-${asset.id}`,
            signal: controller.signal,
            interval: intervalMap[period],
          });
          let fallbackPoints = hist.data.prices
            .map(p => ({ time: p.timestamp, value: p.value }))
            .filter(p => Number.isFinite(p.time) && Number.isFinite(p.value) && p.value > 0)
            .sort((a, b) => a.time - b.time);
          fallbackPoints = sliceRecentPoints(fallbackPoints, CHART_POINTS_LIMIT);
          if (fallbackPoints.length > 0) {
            points = fallbackPoints;
          }
        } catch {
          // ignore fallback errors
        }
      }

      if (!points || points.length === 0) {
        throw new Error('暂无历史数据');
      }

      if (mountedRef.current) {
        setPrices(points);
      }
    } catch (err) {
      if (controller.signal.aborted) {
        return;
      }
      const message = err instanceof Error ? err.message : '历史数据加载失败';
      if (mountedRef.current) {
        setError(message);
        setPrices([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    asset.type,
    aSymbol,
    bondSymbol,
    forexCandidates,
    hkSymbol,
    indexCandidates,
    period,
    supportsPeriod,
  ]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const latestPrice = prices.length > 0 ? prices[prices.length - 1]!.value : 0;

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label="返回"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 18l-6-6 6-6"
            />
          </svg>
        </button>
        <div className="text-lg font-semibold text-gray-900 dark:text-white">
          {asset.name}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {PERIODS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setPeriod(value)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              period === value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            aria-pressed={period === value}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingIndicator type="spinner" size="lg" message="加载中..." />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <ErrorDisplay
              error={{
                id: 'aktools-history-error',
                code: 'SERVER_ERROR',
                message: error,
                userMessage: error,
                timestamp: new Date(),
                retryable: true,
                actions: [
                  {
                    label: '重试',
                    action: fetchHistory,
                    primary: true,
                  },
                ],
              }}
              onDismiss={() => setError(null)}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              {formatPrice(latestPrice)}
            </div>
            <div className="h-64 -ml-3 pr-1">
              <Chart
                data={prices}
                color="#3b82f6"
                height={256}
                formatTime={formatTimeLabel}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
